import type {SeaCampaignResponse} from '@/api/directus';
import type {
  CampaignConfig,
  ContextValue,
  DiscountBadge,
} from '@/modules/common/context/SeaCampaign/models';

import {useBreakpointUp} from '@travel-frontend/core';
import {noop} from 'lodash';
import {useEffect, useMemo, useState} from 'react';

import directus from '@/api/directus';
import seaService from '@/api/seaService';
import {useQueryParams} from '@/modules/common/context/QueryParams';
import {SeaCampaignContext} from '@/modules/common/context/SeaCampaign/SeaCampaignContext';
import {usePageServiceSite} from '@/modules/common/queries/pageService';
import {findContentBlockItem} from '@/modules/common/queries/pageService/utils';
import {createDirectusAssetUrl} from '@/utils/createDirectusAssetUrl';

export const SEA_CAMPAIGN_URL_PARAM = 'sea';

const mapDirectusResponse = (directusResponse: SeaCampaignResponse) => {
  const productBenefitBanners =
    directusResponse.data?.product_benefit_banners?.[0];

  return {
    benefits: {
      headline: productBenefitBanners?.headline || '',
      items: Array.isArray(productBenefitBanners?.benefits)
        ? productBenefitBanners.benefits.map((item) => {
            const benefit = item?.product_benefits_id || {};
            return {
              title: benefit.headline || '',
              keyfact: benefit.keyfact || '',
              description: benefit.tooltipText || '',
              iconUrl: createDirectusAssetUrl(benefit.icon?.filename_disk),
            };
          })
        : [],
    },
  };
};

type Props = React.PropsWithChildren;

export const SeaCampaignProvider: React.FunctionComponent<Props> = ({
  children,
}) => {
  const [queryParams] = useQueryParams();
  const [config, setConfig] = useState<CampaignConfig | null>(null);
  const lgUp = useBreakpointUp('lg');

  const destinationId = queryParams.destinationId || '';
  const slug = queryParams[SEA_CAMPAIGN_URL_PARAM] || '';

  // We know immediately when the campaign is active.
  const active = slug.length > 0;

  const pageServiceQuery = usePageServiceSite();
  const pageServiceConfig = pageServiceQuery.data || null;

  useEffect(() => {
    const abortController = new AbortController();
    let ignore = false;

    if (destinationId && slug) {
      Promise.all([
        seaService.content(destinationId, slug, {
          signal: abortController.signal,
        }),
        directus.seaCampaign({
          abortSignal: abortController.signal,
        }),
      ])
        .then(([seaServiceContentResponse, directusResponse]) => {
          if (
            ignore ||
            !seaServiceContentResponse ||
            !directusResponse ||
            !pageServiceConfig
          ) {
            return;
          }

          const mappedDirectusResponse = mapDirectusResponse(directusResponse);
          const badges = findContentBlockItem<{
            badges: Array<DiscountBadge>;
          }>({
            deviceId: lgUp ? 'lg' : 'sm',
            collectionId: 'block_badges',
            config: pageServiceConfig,
            section: 'spotlight',
          })?.badges;

          setConfig({
            headlinePrefix:
              seaServiceContentResponse.data?.headlinePrefix ?? '',
            subline: seaServiceContentResponse.data?.subline ?? '',
            teaserImageUrl: seaServiceContentResponse.data?.image,
            benefits: mappedDirectusResponse.benefits,
            badges,
          });
        })
        .catch(noop);
    }

    return () => {
      abortController.abort();
      ignore = true;
    };
  }, [destinationId, pageServiceConfig, slug]);

  return (
    <SeaCampaignContext.Provider
      value={useMemo<ContextValue>(
        () => ({
          active,
          slug,
          config,
        }),
        [active, slug, config]
      )}
    >
      {children}
    </SeaCampaignContext.Provider>
  );
};
